<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission)

const loading  = ref(false)
const saving   = ref(false)
const cycles   = ref([])
const showModal = ref(false)
const editId    = ref(null)
const formError = ref('')

const blankForm = () => ({
  title: '', description: '',
  start_date: '', end_date: '',
  review_type: 'annual', status: 'draft',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get('/hr/pms/cycles')
    cycles.value = res.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(load)

const openCreate = () => {
  editId.value = null
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const openEdit = (c) => {
  editId.value = c.id
  form.value = {
    title: c.title, description: c.description ?? '',
    start_date: c.start_date, end_date: c.end_date,
    review_type: c.review_type, status: c.status,
  }
  formError.value = ''
  showModal.value = true
}

const save = async () => {
  if (!form.value.title || !form.value.start_date || !form.value.end_date) {
    formError.value = 'Title, start date and end date are required.'
    return
  }
  try {
    saving.value = true; formError.value = ''
    if (editId.value) {
      await axios.put(`/hr/pms/cycles/${editId.value}`, form.value)
    } else {
      await axios.post('/hr/pms/cycles', form.value)
    }
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save'
  } finally { saving.value = false }
}

const activate = async (id) => {
  if (!confirm('Activate this cycle? It will become the active appraisal period.')) return
  await axios.post(`/hr/pms/cycles/${id}/activate`)
  load()
}

const deleteCycle = async (id) => {
  if (!confirm('Delete this cycle?')) return
  await axios.delete(`/hr/pms/cycles/${id}`)
  load()
}

const statusClass = (s) => ({
  draft: 'badge--grey', active: 'badge--green',
  closed: 'badge--blue', archived: 'badge--red'
}[s] ?? '')

const typeLabel = (t) => ({ annual: 'Annual', mid_year: 'Mid-Year', quarterly: 'Quarterly', probation: 'Probation' }[t] ?? t)
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div class="toolbar">
        <h2 class="page-title">Appraisal Cycles</h2>
        <button v-if="permission?.manage_pms_cycles" class="btn-primary" @click="openCreate">+ New Cycle</button>
      </div>

      <div v-if="loading" class="empty">Loading…</div>
      <div v-else-if="!cycles.length" class="empty">No appraisal cycles yet. Create one to get started.</div>
      <div v-else class="grid">
        <div v-for="c in cycles" :key="c.id" class="card">
          <div class="card-head">
            <span class="cycle-title">{{ c.title }}</span>
            <span :class="['badge', statusClass(c.status)]">{{ c.status }}</span>
          </div>
          <div class="cycle-type">{{ typeLabel(c.review_type) }}</div>
          <div class="cycle-dates">{{ c.start_date }} → {{ c.end_date }}</div>
          <div v-if="c.description" class="cycle-desc">{{ c.description }}</div>
          <div class="card-actions">
            <router-link :to="$url(`/hr/performance/cycles/${c.id}`)" class="btn-link">View Detail</router-link>
            <button v-if="permission?.manage_pms_cycles && c.status === 'draft'" class="btn-link" @click="activate(c.id)">Activate</button>
            <button v-if="permission?.manage_pms_cycles" class="btn-link" @click="openEdit(c)">Edit</button>
            <button v-if="permission?.manage_pms_cycles && c.status === 'draft'" class="btn-link btn-link--red" @click="deleteCycle(c.id)">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editId ? 'Edit' : 'New' }} Appraisal Cycle</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group form-group--full">
              <label>Title *</label>
              <input v-model="form.title" placeholder="e.g. Annual Review 2026" />
            </div>
            <div class="form-group">
              <label>Start Date *</label>
              <input type="date" v-model="form.start_date" />
            </div>
            <div class="form-group">
              <label>End Date *</label>
              <input type="date" v-model="form.end_date" />
            </div>
            <div class="form-group">
              <label>Review Type</label>
              <select v-model="form.review_type">
                <option value="annual">Annual</option>
                <option value="mid_year">Mid-Year</option>
                <option value="quarterly">Quarterly</option>
                <option value="probation">Probation</option>
              </select>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div class="form-group form-group--full">
              <label>Description</label>
              <textarea v-model="form.description" rows="2" placeholder="Optional notes…" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.empty { text-align: center; color: #888; padding: 60px; font-size: 14px; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
.card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 18px 20px; transition: box-shadow 0.15s; }
.card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
.card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.cycle-title { font-size: 15px; font-weight: 700; color: #1a2a45; }
.cycle-type { font-size: 12px; color: #888; margin-bottom: 4px; text-transform: capitalize; }
.cycle-dates { font-size: 13px; color: #556; margin-bottom: 6px; }
.cycle-desc { font-size: 12.5px; color: #888; margin-bottom: 8px; }
.card-actions { display: flex; gap: 8px; border-top: 1px solid #f0f3fa; padding-top: 10px; margin-top: 8px; flex-wrap: wrap; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--grey  { background: #f0f3fa; color: #556; }
.badge--green { background: #d1e7dd; color: #0f5132; }
.badge--blue  { background: #cfe2ff; color: #084298; }
.badge--red   { background: #f8d7da; color: #842029; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; text-decoration: none; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 580px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select, .form-group textarea { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; resize: vertical; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
